import {FC, useEffect, useState,} from "react";
import {useNavigate} from "react-router";
import {Helmet} from "react-helmet";
import {ConfirmModal, FormCol, FormText, useLoader, useMessage} from "backoffice/ui/Components";
import {useTRPC} from "backoffice/ui/App";
import {Breadcrumb, Button, Col, Container, Row, Table, ToggleButton, ToggleButtonGroup} from "react-bootstrap";
import {useSearchParams} from "react-router-dom";
import {useImmer} from "use-immer";
import {AppRouterOutput} from "backoffice/api/AppRouter";
import {useImmerChangeHandler} from "src/utils/react/hooks.tsx";
import {Currency, LoanManager} from "src/codes";


type ConditionType = AppRouterOutput['coolingOff']['params']
type ResultType = AppRouterOutput['coolingOff']['list'];


/**
 *
 * @param defaultCondition
 * @constructor
 */
export const CoolingOffListPageImpl: FC<{ defaultCondition: ConditionType }> = ({defaultCondition}) => {

  const Loader   = useLoader(),
        Message  = useMessage(),
        Navigate = useNavigate(),
        tRPC     = useTRPC();

  // ステート
  const [query, setQuery]         = useSearchParams(),
        [condition, setCondition] = useImmer<ConditionType>(defaultCondition),
        [result, setResult]       = useState<ResultType>({}),
        [managerId, setManagerId] = useState(''),
        [modal, setModal]         = useState('');


  const conditionHandler = useImmerChangeHandler(setCondition);

  // 検索
  useEffect(() => {
    Loader.task(() => tRPC.coolingOff.list.query(condition), 500)
          .then(response => setResult(response))
          .catch(err => {
            console.error(err);
            Message.error(err);
          });
  }, [query]);

  // 検索
  const handleSearch = () => {
    const cond = condition ?? {} as Record<string, string>;
    for (const [k, v] of Object.entries(cond)) {
      if (v.trim()) {
        query.set(k, v);
      } else {
        query.delete(k);
      }
    }
    query.set('ts', String(Date.now()));
    setQuery(query);
  };

  // 登録
  const handleRegister = () => {
    const {list, total} = result[managerId],
          request       = {...condition, managerId, count: list.length, total};

    Loader.task(() => tRPC.coolingOff.register.mutate(request), 500)
          .then(() => Message.show('完了しました'))
          .then(() => setModal(''))
          .then(() => handleSearch())
          .catch(err => {
            console.error(err);
            Message.error(err);
          });
  };


  return (
      <>
        <Helmet>
          <title>クーリング・オフ管理</title>
        </Helmet>

        <div className={'container'}>
          <Breadcrumb>
            <Breadcrumb.Item active>資金管理</Breadcrumb.Item>
            <Breadcrumb.Item active>クーリング・オフ管理</Breadcrumb.Item>
          </Breadcrumb>
        </div>


        <Container className={'mt-5'}>
          <Col lg={{offset: 2, span: 8}}
               xl={{offset: 3, span: 6}}
               xxl={{offset: 3, span: 6}}>

            <Row className={''}>
              <FormCol inputId={'since'} className={'py-1'}
                       title={'申請期間（開始）'}
                       size={{xs: [4, 8]}}>

                <FormText name={'since'} type={'date'}
                          value={condition?.since ?? ''}
                          onChange={conditionHandler}/>
              </FormCol>

              <FormCol inputId={'until'} className={'py-1'}
                       title={'申請期間（終了）'}
                       size={{xs: [4, 8]}}>

                <FormText name={'until'} type={'date'}
                          value={condition?.until ?? ''}
                          onChange={conditionHandler}/>
              </FormCol>
            </Row>


            <Row>
              <FormCol inputId={'status'} className={'py-1'}
                       title={'ステータス'}
                       size={{xs: [4, 8]}}>

                <ToggleButtonGroup type={"radio"}
                                   name={"yet"}
                                   value={condition?.yet}
                                   onChange={yet => setCondition(prev => ({...prev, yet}))}>

                  <ToggleButton id={"yet-1"}
                                value={"1"}
                                style={{width: "8rem"}}
                                size={'sm'}
                                checked={condition?.yet === "1"}>
                    未処理
                  </ToggleButton>

                  <ToggleButton id={"yet-none"}
                                value={""}
                                style={{width: "8rem"}}
                                size={'sm'}
                                checked={condition?.yet === ""}>
                    処理済み
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormCol>
            </Row>

            <Row style={{marginTop: 20}}>
              <Col xs={{span: 12}} style={{padding: 10}}>
                <Button variant="outline-primary" className={'w-100'} onClick={handleSearch} size={'sm'}>
                  検索する
                </Button>
              </Col>
            </Row>
          </Col>
        </Container>

        <Container className={'mt-5'}>
          <Col lg={{offset: 2, span: 8}}>
            <Table>
              <thead>
              <tr>
                <th className={'text-start'}>営業者</th>
                <th className={'text-end  '}>確認金額</th>
                <th className={'text-end  '}>総額</th>
                <th className={'text-end  '}>件数</th>
                <th>&nbsp;</th>
              </tr>
              </thead>

              <tbody>
              {Object.keys(result).length === 0 && (
                  <tr>
                    <td colSpan={4}>対象データはありません</td>
                  </tr>
              )}
              {Object.entries(result).map(([managerId, {total, check, list}]) => (
                  <tr key={managerId}>
                    <td className={'text-start '}>{LoanManager.find(managerId).value}</td>
                    <td className={'text-end   '}>{Currency.JPY.format(check)}</td>
                    <td className={'text-end   '}>{Currency.JPY.format(total)}</td>
                    <td className={'text-end   '}>{list.length.withComma} 件</td>
                    <td className={'text-center'}>
                      <Button variant={'secondary'}
                              size={'sm'}
                              onClick={() => {
                                setManagerId(managerId);
                                setModal('confirm')
                              }}
                              disabled={condition?.yet === ""}
                      >
                        登録
                      </Button>
                    </td>
                  </tr>
              ))}
              </tbody>
            </Table>
          </Col>
        </Container>

        <ConfirmModal show={modal === 'confirm'}
                      keyword={'クーリングオフ'}
                      confirmLabel={'登録'}
                      onConfirm={handleRegister}
                      onCancel={() => setModal('')}>

          クーリング・オフの資金移動の登録を行い、顧客残高に反映します。
        </ConfirmModal>
      </>
  );
}


// デフォルト値取得用ラッパー
export const CoolingOffListPage: FC = () => {
  const Loader  = useLoader(),
        Message = useMessage(),
        tRPC    = useTRPC();

  const [defaultCondition, setDefaultCondition] = useState<ConditionType | null>(null),
        [query]                                 = useSearchParams();

  // 初期化
  useEffect
  (() => {
    Loader.task(() => tRPC.coolingOff.params.query(), 500)
          .then(response => {
            setDefaultCondition({
              since: query!.get('since') || response.since,
              until: query!.get('until') || response.until,
              yet  : query!.get('yet') || response.yet,
            })
          })
          .catch(err => {
            console.error(err);
            Message.error(err);
          });
  }, []);


  if (defaultCondition === null) {
    return null;
  }
  return <CoolingOffListPageImpl defaultCondition={defaultCondition}/>
}
